import React, { useState } from 'react'
import PropTypes from 'prop-types';

import formatDate from './../../libs/formatDate';
import ValidationText from '../../ui/validation/ValidationText';
import GridDatePicker from '../../ui/datePicker/GridDatePicker';

/**
 * EditDatepickerColumn component for rendering a date picker with validation.
 * @param {object} props - Component props.
 * @param {object} props.rowData - Data for the current row.
 * @param {object} props.col - Column configuration.
 * @param {object} props.editColumn - Editable column configuration.
 * @param {number} props.rowIndex - Index of the current row.
 * @param {function} props.onChange - Function to handle changes.
 * @param {object} props.updatedData - Updated row data.
 * @param {object} props.errors - Validation errors.
 */
const EditDatepickerColumn = ({
  rowData,
  col,
  editColumn,
  rowIndex,
  onBlur,
  onChange,
  updatedData,
  errors,
}) => {
  const [selectedDate, setSelectedDate] = useState(
    rowData[col.fieldName] ? new Date(rowData[col.fieldName]) : null
  );

  const {
    isDisable,
    placeholder,
    colConfig = {},// Destructure colConfig with a default value of an empty object
  } = editColumn;


  const dateFormat = col?.colSettings?.format || 'MM/DD/YYYY';

  /**
   * Handles date selection change.
   * @param {Date} date - The new date selected.
   */
  const handleDateChange = (date) => {
    setSelectedDate(date);

    if (onChange) {
      const formattedDate = date ? formatDate(date, dateFormat) : null;
      const newRowData = updatedData
        ? { ...updatedData, [col.fieldName]: formattedDate }
        : { ...rowData, [col.fieldName]: formattedDate };

      onChange(rowIndex, newRowData, col?.fieldName);
    }
  };

  /**
     * Handles blur event of input value (field loses focus).
     * @param {object} e - Event object from input blur.
     */
  const handleBlur = (e) => {

    const newDateValue = e.target.value ? new Date(e.target.value) : null;

    setSelectedDate(newDateValue);

    if (onBlur) {
      const newRowData = updatedData
        ? { ...updatedData, [col.fieldName]: newDateValue }
        : { ...rowData, [col.fieldName]: newDateValue };

      onBlur(rowIndex, newRowData, col?.fieldName);
    }
  };

  const hasError = !!errors?.[rowIndex]?.[col.fieldName];
  const errorMessage = errors?.[rowIndex]?.[col.fieldName] || '';

  return (
    <>
      <GridDatePicker
        selected={selectedDate}
        name={col.name}
        placeholder={placeholder}
        onChange={handleDateChange}
        onBlur={handleBlur}
        isDisable={isDisable}
        inputFormat={colConfig?.format}
      />
      {hasError && <ValidationText error={errorMessage || ' '} className="error-message" />}
    </>
  );
};

EditDatepickerColumn.propTypes = {
  rowData: PropTypes.object.isRequired,
  col: PropTypes.shape({
    fieldName: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    colSettings: PropTypes.shape({
      format: PropTypes.string,
    }),
  }).isRequired,
  editColumn: PropTypes.shape({
    onBlur: PropTypes.func,
    isDisable: PropTypes.bool,
    placeholder: PropTypes.string,
    colConfig: PropTypes.shape({
      format: PropTypes.string,
    }),
  }).isRequired,
  rowIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  updatedData: PropTypes.object,
  errors: PropTypes.object,
};

export default EditDatepickerColumn;